import { withAsyncDeadline } from '../shared/async-deadline';
// One recovery worker per store key. A joining delta raises the required version;
// sharing an HTTP request must never discard the fact that newer truth exists.
export function createStateRecovery(
    kind: 'encounter' | 'caster',
    read: (id: string, signal: AbortSignal) => Promise<number | undefined>,
): (id: string, version?: number) => Promise<void> {
    const pending = new Map<string, { version?: number; promise: Promise<void> }>();
    return (id, version) => {
        const held = pending.get(id);
        if (held) {
            if (version !== undefined) held.version = Math.max(held.version ?? version, version);
            return held.promise;
        }
        const state = { version, promise: Promise.resolve() };
        // Defer until the worker is registered, including when read() throws synchronously.
        state.promise = Promise.resolve().then(async () => {
            let error: unknown;
            for (let attempt = 0; attempt < 3; attempt++) {
                try {
                    const installed = await withAsyncDeadline(signal => read(id, signal), 180000);
                    if (state.version === undefined || (installed !== undefined && installed >= state.version)) {
                        report('recovered', installed);
                        return;
                    }
                    error = new Error(`Recovery behind observed version ${state.version} (received ${installed})`);
                } catch (err) {
                    error = err;
                    if (err instanceof DOMException && err.name === 'TimeoutError') break;
                }
                if (attempt < 2) await new Promise(resolve => setTimeout(resolve, 150 * 2 ** attempt));
            }
            console.error(`Failed to recover ${kind} state:`, id, error);
            report('failed');
        }).finally(() => pending.delete(id));
        function report(status: 'recovered' | 'failed', installedVersion?: number): void {
            if (typeof window.dispatchEvent === 'function' && typeof CustomEvent === 'function') {
                window.dispatchEvent(new CustomEvent('ft:ws-state-recovery', {
                    detail: { kind, id, status, requiredVersion: state.version, installedVersion },
                }));
            }
        }
        pending.set(id, state);
        return state.promise;
    };
}
